import * as React from "react"
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner"
import type { ToasterProps as SonnerToasterProps } from "sonner"
import { cn } from "../../utils"
import { Check, Info, Warning, X, CircleNotch } from "@phosphor-icons/react"

/**
 * Extended Toaster Props for full customization
 */
export interface SonnerProps extends Omit<SonnerToasterProps, 'theme'> {
  theme?: "light" | "dark" | "system"
  className?: string
  position?: SonnerToasterProps["position"]
  closeButton?: boolean
  duration?: number
  gap?: number
  offset?: string
  expand?: boolean
  icons?: SonnerToasterProps["icons"]
  richColors?: boolean
  toastOptions?: SonnerToasterProps["toastOptions"]
  visibleToasts?: number
}

/**
 * Minimal toast styles — compact, modern, Notion/Linear inspired
 */
const sonnerStyles = `
  [data-sonner-toaster] {
    z-index: 2147483647 !important;
  }

  [data-sonner-toast] {
    background: #ffffff !important;
    color: #1a1a1a !important;
    border: 1px solid #e8e8e8 !important;
    border-radius: 10px !important;
    padding: 12px 16px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04) !important;
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    font-size: 14px !important;
    min-width: 340px !important;
    max-width: 420px !important;
    min-height: unset !important;
  }

  [data-sonner-toast][data-type="error"],
  [data-sonner-toast][data-type="success"],
  [data-sonner-toast][data-type="warning"],
  [data-sonner-toast][data-type="info"],
  [data-sonner-toast][data-type="loading"] {
    background: #ffffff !important;
    color: #1a1a1a !important;
    border-color: #e8e8e8 !important;
  }

  [data-sonner-toast] [data-icon] {
    margin: 0 !important;
    width: auto !important;
    height: auto !important;
  }

  [data-sonner-toast] [data-title],
  .sonner-title {
    color: #1a1a1a !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    line-height: 1.4 !important;
  }

  [data-sonner-toast] [data-description],
  .sonner-description {
    color: #6b7280 !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    line-height: 1.4 !important;
    margin-top: 1px !important;
  }

  [data-sonner-toast] [data-content] {
    display: flex !important;
    flex-direction: column !important;
    gap: 1px !important;
  }

  /* Close button — only visible on hover */
  [data-sonner-toast] [data-close-button] {
    position: absolute !important;
    top: 6px !important;
    right: 6px !important;
    left: unset !important;
    width: 20px !important;
    height: 20px !important;
    border-radius: 6px !important;
    background: transparent !important;
    border: none !important;
    color: #9ca3af !important;
    opacity: 0 !important;
    transition: opacity 150ms ease, background 150ms ease, color 150ms ease !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    padding: 0 !important;
  }

  [data-sonner-toast]:hover [data-close-button] {
    opacity: 1 !important;
  }

  [data-sonner-toast] [data-close-button]:hover {
    background: #f3f4f6 !important;
    color: #4b5563 !important;
  }

  [data-sonner-toast] [data-close-button] svg {
    width: 12px !important;
    height: 12px !important;
  }

  /* Double-circle icon styles */
  .sonner-icon-outer {
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sonner-icon-inner {
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Success icon colors */
  .sonner-icon-success-outer { background-color: #34C75933; }
  .sonner-icon-success-inner { background-color: #34C759; }

  /* Error icon colors */
  .sonner-icon-error-outer { background-color: #FF383C33; }
  .sonner-icon-error-inner { background-color: #FF383C; }

  /* Warning icon colors */
  .sonner-icon-warning-outer { background-color: #FFF3E0; }
  .sonner-icon-warning-inner { background-color: #F97316; }

  /* Info icon colors */
  .sonner-icon-info-outer { background-color: #0088FF33; }
  .sonner-icon-info-inner { background-color: #0088FF; }

  /* Loading icon colors */
  .sonner-icon-loading-outer { background-color: #F3F4F6; }
  .sonner-icon-loading-inner { background-color: #9CA3AF; }

  .sonner-icon-spin {
    animation: sonner-spin 1s linear infinite;
  }

  @keyframes sonner-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`

/**
 * Double-circle icon components (outer ring + inner circle)
 */
const ToastIcon = ({
  children,
  outerClassName,
  innerClassName
}: {
  children: React.ReactNode
  outerClassName: string
  innerClassName: string
}) => (
  <div className={outerClassName}>
    <div className={innerClassName}>
      {children}
    </div>
  </div>
)

const SuccessIcon = () => (
  <ToastIcon outerClassName="sonner-icon-outer sonner-icon-success-outer" innerClassName="sonner-icon-inner sonner-icon-success-inner">
    <Check weight="bold" size={18} color="#ffffff" />
  </ToastIcon>
)

const ErrorIcon = () => (
  <ToastIcon outerClassName="sonner-icon-outer sonner-icon-error-outer" innerClassName="sonner-icon-inner sonner-icon-error-inner">
    <X weight="bold" size={18} color="#ffffff" />
  </ToastIcon>
)

const WarningIcon = () => (
  <ToastIcon outerClassName="sonner-icon-outer sonner-icon-warning-outer" innerClassName="sonner-icon-inner sonner-icon-warning-inner">
    <Warning weight="bold" size={18} color="#ffffff" />
  </ToastIcon>
)

const InfoIcon = () => (
  <ToastIcon outerClassName="sonner-icon-outer sonner-icon-info-outer" innerClassName="sonner-icon-inner sonner-icon-info-inner">
    <Info weight="bold" size={18} color="#ffffff" />
  </ToastIcon>
)

const LoadingIcon = () => (
  <ToastIcon outerClassName="sonner-icon-outer sonner-icon-loading-outer" innerClassName="sonner-icon-inner sonner-icon-loading-inner">
    <CircleNotch size={18} color="#ffffff" className="sonner-icon-spin" />
  </ToastIcon>
)

/**
 * Sonner Toaster Component — compact, modern toast notifications.
 * Place once at the root of your app.
 *
 * @example
 * ```tsx
 * import { Sonner } from "@t-one-internal/design-system"
 *
 * function App() {
 *   return (
 *     <>
 *       <YourApp />
 *       <Sonner position="top-right" />
 *     </>
 *   )
 * }
 * ```
 */
function Sonner({
  className,
  position = "top-right",
  closeButton = true,
  duration = 4000,
  gap = 10,
  offset = "16px",
  expand = false,
  visibleToasts = 3,
  toastOptions,
  icons: _icons,
  ...props
}: SonnerProps) {
  const customIcons = {
    success: <SuccessIcon />,
    error: <ErrorIcon />,
    warning: <WarningIcon />,
    info: <InfoIcon />,
    loading: <LoadingIcon />,
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: sonnerStyles }} />
      <SonnerToaster
        {...props}
        position={position}
        closeButton={closeButton}
        duration={duration}
        gap={gap}
        offset={offset}
        expand={expand}
        visibleToasts={visibleToasts}
        className={cn("toaster group", className)}
        icons={customIcons}
        toastOptions={{
          unstyled: true,
          classNames: {
            title: 'sonner-title',
            description: 'sonner-description',
            toast: 'sonner-toast',
          },
          ...toastOptions,
        }}
      />
    </>
  )
}

/**
 * Format a date for toast description
 * Returns format: "Friday, Aug 10, 2025 at 5:57 PM"
 */
const formatToastDate = (date: Date): string => {
  const dateStr = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateStr} at ${timeStr}`;
};

/**
 * Toast options
 */
interface ToastOptions {
  description?: string;
  duration?: number;
  id?: string | number;
}

// Tracks active interceptor-level toast ids. When an interceptor toast fires,
// subsequent error toasts without an explicit id are suppressed briefly to
// prevent duplicate "Error" toasts from component catch blocks.
let _interceptorToastActive = false;
let _interceptorToastTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Mark that an interceptor-level error toast was just shown.
 * Call this from axios interceptors right after showing a toast.
 * Suppresses generic error toasts for a short window (500ms).
 */
const markInterceptorToast = () => {
  _interceptorToastActive = true;
  if (_interceptorToastTimer) clearTimeout(_interceptorToastTimer);
  _interceptorToastTimer = setTimeout(() => {
    _interceptorToastActive = false;
  }, 500);
};

/**
 * Toast notification utility — uses native Sonner API for proper
 * animations, accessibility, and dismiss behavior.
 *
 * @example
 * ```tsx
 * import { showToast } from "@t-one-internal/design-system"
 *
 * showToast.success('Saved!', { description: 'Your changes have been saved' })
 * showToast.error('Failed', { description: 'Please try again' })
 * showToast.warning('Warning', { description: 'This cannot be undone' })
 * showToast.info('Tip', { description: 'You can drag to reorder' })
 * ```
 */
const showToast = {
  success: (title: string, options?: ToastOptions) => {
    return sonnerToast.success(title, {
      id: options?.id,
      description: options?.description,
      duration: options?.duration ?? 4000,
    });
  },
  error: (title: string, options?: ToastOptions) => {
    // If an interceptor toast is active, suppress generic error toasts
    // (those without an explicit id) to prevent duplicates
    if (_interceptorToastActive && !options?.id) {
      return;
    }
    return sonnerToast.error(title, {
      id: options?.id,
      description: options?.description,
      duration: options?.duration ?? 4000,
    });
  },
  warning: (title: string, options?: ToastOptions) => {
    return sonnerToast.warning(title, {
      id: options?.id,
      description: options?.description,
      duration: options?.duration ?? 4000,
    });
  },
  info: (title: string, options?: ToastOptions) => {
    return sonnerToast.info(title, {
      id: options?.id,
      description: options?.description,
      duration: options?.duration ?? 4000,
    });
  },
  dismiss: sonnerToast.dismiss,
};

export { Sonner, sonnerToast, formatToastDate, showToast, markInterceptorToast }
